// A small history router: the app has a few screens and no need for a library.
// Home is / for tonight and /archive/{date} for a past night; the Horoscope is
// /horoscope and /archive/{date}/horoscope; play is /play/{levelId}, with the
// date appended on a past night (Site Map; a night holds any set of levels,
// so the level's id names the play, not its kind).
import { useSyncExternalStore } from 'react';

const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', notify);
}

export function navigate(path: string, replace = false): void {
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  notify();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function usePath(): string {
  return useSyncExternalStore(subscribe, () => location.pathname, () => '/');
}

export interface PlayRoute {
  levelId: string;
  /** A past night's date; absent for tonight. */
  date?: string;
}

const DATE = '(\\d{4}-\\d{2}-\\d{2})';
const LEVEL_ID = '([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})';
const PLAY_RE = new RegExp(`^/play/${LEVEL_ID}(?:/${DATE})?/?$`, 'i');
const NIGHT_RE = new RegExp(`^/archive/${DATE}/?$`);
const HOROSCOPE_RE = new RegExp(`^(?:/archive/${DATE})?/horoscope/?$`);

export function matchPlayRoute(path: string): PlayRoute | null {
  const m = PLAY_RE.exec(path);
  if (!m) return null;
  const levelId = m[1]!.toLowerCase();
  return m[2] ? { levelId, date: m[2] } : { levelId };
}

export function playPath(levelId: string, date?: string | null): string {
  return date ? `/play/${levelId}/${date}` : `/play/${levelId}`;
}

/**
 * Tonight's play address names no night, so the night it opened on rides in
 * the history entry's state, which survives a reload: a reload after the
 * rollover (the browser's own, as when it drops a background tab) reopens
 * that night's play instead of asking for the level on the next night, where
 * it is not. The address itself stays as it is, so the play is not reopened.
 */
export function notePlayNight(levelId: string, date: string): void {
  const state = (history.state ?? {}) as Record<string, unknown>;
  history.replaceState({ ...state, playNight: { levelId, date } }, '');
}

/** The night tonight's play on a level opened on, from this history entry. */
export function playNightOf(levelId: string): string | undefined {
  const noted = (history.state as { playNight?: { levelId: string; date: string } } | null)?.playNight;
  return noted?.levelId === levelId ? noted.date : undefined;
}

/** The past night the home screen shows, or null for tonight. */
export function matchNightRoute(path: string): string | null {
  return NIGHT_RE.exec(path)?.[1] ?? null;
}

/** Home on a night: / for tonight, /archive/{date} for a past one. */
export function nightPath(date?: string | null): string {
  return date ? `/archive/${date}` : '/';
}

export interface HoroscopeRoute {
  /** A past night's date; null for tonight. */
  date: string | null;
}

/** The Horoscope screen, on the home half of the sky. */
export function matchHoroscopeRoute(path: string): HoroscopeRoute | null {
  const m = HOROSCOPE_RE.exec(path);
  return m ? { date: m[1] ?? null } : null;
}

/** The night's Horoscope: /horoscope for tonight, /archive/{date}/horoscope for a past one. */
export function horoscopePath(date?: string | null): string {
  return date ? `/archive/${date}/horoscope` : '/horoscope';
}

/** Home on the night of where the player is now: a play or a Horoscope on a past night goes home to that night. */
export function homePath(): string {
  const path = location.pathname;
  return nightPath(matchPlayRoute(path)?.date ?? matchNightRoute(path) ?? matchHoroscopeRoute(path)?.date);
}
